import React, { memo, useCallback, useEffect, useState, useContext, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import { useSelector, shallowEqual } from 'react-redux'
import { createSelector } from 'reselect'
import { pathToRegexp } from 'path-to-regexp'

import { Grid, Button } from '@material-ui/core'
import { useTheme } from '@material-ui/styles'

import { TYPES } from '~/actions'
import track from '~/track'

import { AppRouterContext } from '~/Router'
import AddBusinessFilledIcon from '~/components/AddBusinessFilledIcon'
import DialogRetailForm from '~/components/DialogRetailForm'
import useViewportSize from '~/components/useViewportSize'
import RenderDesktop from '~/components/Desktop'
import RenderMobile from '~/components/Mobile'
import ProductList from '~/components/ProductList'
import StoreList from '~/components/StoreList'
import HomePage from '~/components/HomePage'
import { useLiterals } from '~/components/Literals'

import MapContainer from '../Map'
import StoreDetailsAlone from '../StoreDetails/StoreDetailsAlone'
import Logo from '~/assets/new_gofind/logoMap.svg'
import { inIframe } from '~/utils'
import localStorage from '~/localStorage'
import CookieBanner from '~/components/Banner/Cookie'

const useStyles = makeStyles((theme) => ({
  logoGofind: {
    zIndex: 99,
    position: 'absolute',
    bottom: 20,
    right: 8,
    width: 100
  },
  resselButton: {
    height: 40,
    zIndex: 999
  }
}))

const selector = createSelector(
  (state) => state.app.view,
  (state) => state.app.mobileBottomShow,
  (state) => state.app.currentProduct,
  (state) => state.app.showStoreInformation,
  (...args) => args
)

function AppContainer({ position, config }) {
  const { state, dispatch } = useContext(AppRouterContext)

  const classes = useStyles()
  const viewport = useViewportSize()
  const theme = useTheme()

  const { getLiteral } = useLiterals()

  const [view, showBottom, currentProduct] = useSelector(selector, shallowEqual)
  const [storeHover, setStoreHover] = useState(0)
  const [isMobile, setIsMobile] = useState(viewport.width < theme.gofind.desktopMaxWidth)
  const [retailOpen, setRetailOpen] = useState(false)

  const { hideRetailButton, retailLink, retailLabel, cnpj, generic } = config
  const enableRetailButton = position.customLocation || position.user

  let FORM_WAS_SEND = false

  const urlOrigin = window.location.toString()

  const getUrlRegex = (url) => {
    return pathToRegexp(url, null, { sensitive: true })
  }

  const isUrlAllProducts = getUrlRegex('/produto/todos').test(location.pathname)
  const isUrlStoreWithCnpj = getUrlRegex('/loja/:cnpj').test(location.pathname)
  const isUrlProductList = getUrlRegex('/').test(location.pathname)

  const getMapSize = (viewport) => viewport.height * 0.4
  const getFullMap = (viewport, view) => {
    const { showContextViewStore = false, showContextViewEstablishment = false } = config

    const isContextStoreOrEstablish = showContextViewStore || showContextViewEstablishment
    const viewPage = [TYPES.VIEW_PRODUCTS, TYPES.VIEW_SEARCH_PRODUCTS].includes(view) //false

    const calcDiffHeightBasedCurrentPageOrContextLocator =
      (isUrlAllProducts || isUrlStoreWithCnpj) && !isContextStoreOrEstablish
        ? 108
        : viewPage
        ? 118
        : 124

    return viewport.height - calcDiffHeightBasedCurrentPageOrContextLocator
  }

  const handleToggle = useCallback(() => {
    if (isMobile) {
      if (state.isListVisible) {
        dispatch({ current: 0, offset: 0, isListVisible: false })
        return
      }

      dispatch({
        current: getFullMap(viewport, view) - 40, // height - 108 - 40
        offset: getFullMap(viewport, view) - 40,
        isListVisible: true
      })

      if (view === TYPES.VIEW_PRODUCTS)
        return track('clickMapaProdutos', { category: 'mapa', name: 'products' })

      return track('clickMapaLoja', { category: 'mapa', name: 'store' })
    }
  }, [state])

  const handleRetail = () => {
    track('queroRevender', { category: 'menu' })
    setRetailOpen(true)
  }

  const handleRetailDone = () => {
    FORM_WAS_SEND = true
    setRetailOpen(false)
  }

  useEffect(() => {
    if (view === TYPES.VIEW_PRODUCTS)
      dispatch({
        current: getFullMap(viewport, view),
        offset: getFullMap(viewport, view)
      })

    if (view === TYPES.VIEW_STORES)
      dispatch({
        current: getFullMap(viewport, view) - 40,
        offset: getFullMap(viewport, view) - 40
      })
  }, [view])

  useEffect(() => setIsMobile(viewport.width < theme.gofind.desktopMaxWidth), [viewport])

  useEffect(() => {
    if (view === TYPES.VIEW_PRODUCTS)
      dispatch({
        current: getFullMap(viewport, view),
        offset: getFullMap(viewport, view)
      })

    window.addEventListener('beforeinstallprompt', (e) => {
      track('PWA_USER_INSTALL_ASKED')

      e.userChoice.then((choiceResult) => {
        track('PWA_USER_INSTALL_CHOICERESULT', { name: choiceResult.outcome })
      })
    })
  }, [])

  if (view === TYPES.VIEW_TERM) return

  if (view === TYPES.VIEW_HOME_PAGE) {
    return <HomePage position={position} />
  }

  if (view === TYPES.VIEW_STORE_DETAILS_ALONE) {
    return <StoreDetailsAlone config={config} position={position} />
  }

  const children = (
    <>
      {[TYPES.VIEW_PRODUCTS, TYPES.VIEW_SEARCH_PRODUCTS].indexOf(view) !== -1 && (
        <ProductList position={position} />
      )}

      {view === TYPES.VIEW_STORES && (
        <StoreList
          currentProduct={currentProduct}
          storeHover={storeHover}
          setHover={setStoreHover}
          position={position}
        />
      )}
    </>
  )

  const defaultProps = {
    viewport,
    view,
    storeHover,
    setStoreHover,
    children,
    position,
    config
  }

  const gofindPublicDomains = [
    'pertinhodemim.com',
    'pertinhodemim.app',
    'aquiperto.app',
    'localhost'
  ]
  const hostname = window.location.hostname

  const isSomeGofindAppUrl = gofindPublicDomains.some(
    (domain) => hostname.includes(domain) && !hostname.includes('custom')
  )

  return (
    <div
      style={{
        position: 'relative',
        width: viewport.width,
        height: viewport.height
      }}
    >
      {!isMobile && <RenderDesktop {...defaultProps} />}

      {isMobile && (
        <RenderMobile
          {...defaultProps}
          state={state}
          dispatch={dispatch}
          handleToggle={handleToggle}
        />
      )}

      {position.user?.lat && position.user?.lng ? (
        <MapContainer
          showBottom={showBottom}
          view={view}
          position={position}
          showMapPlaceholder={position.showMapPlaceholder}
          mobile={isMobile}
          storeHover={storeHover}
          setStoreHover={setStoreHover}
          state={state}
          dispatch={dispatch}
          config={config}
          currentProduct={currentProduct}
        />
      ) : !urlOrigin.includes('onlineStore') ? (
        <img
          src="https://s3.sa-east-1.amazonaws.com/gofind.img.repo/bg-mapa.webp"
          alt="Preview Map"
          width="100%"
          height="100%"
          style={{ objectFit: 'cover' }}
        />
      ) : null}

      {view === 'VIEW_EMPTY' && isMobile && (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.gofind.online/pertinhodemim?utm_source=Pertinhodemim%20Logo&utm_medium=mapa&utm_campaign=logo%20map"
        >
          <img src={Logo} className={classes.logoGofind} />
        </a>
      )}

      <DialogRetailForm
        open={retailOpen}
        setRetailOpen={setRetailOpen}
        onDone={handleRetailDone}
        position={position}
      />

      <Grid
        style={{
          position: 'absolute',
          display: isMobile && state.current >= getMapSize(viewport) ? 'none' : 'block',
          right: isMobile ? (isUrlProductList ? 11 : 0) : 12,
          ...(isMobile ? (isUrlProductList ? { bottom: 4 } : { bottom: 0 }) : { top: 12 })
        }}
      >
        {!FORM_WAS_SEND &&
          !hideRetailButton &&
          (generic || !cnpj ? (
            <Button
              className={classes.resselButton}
              variant="contained"
              color="primary"
              target="_blank"
              href={retailLink || 'https://empresas.gofind.online/'}
              onClick={() =>
                track('souEmpresa', {
                  name: retailLabel || getLiteral('2056'),
                  category: 'menu'
                })
              }
            >
              {retailLabel || getLiteral('2056')}
            </Button>
          ) : retailLink ? (
            <Button
              className={classes.resselButton}
              variant="contained"
              color="primary"
              target="_blank"
              href={retailLink}
              onClick={() => track('queroRevender', { name: retailLink, category: 'menu' })}
              disabled={!enableRetailButton}
            >
              {getLiteral('1870')}
            </Button>
          ) : (
            <Button
              className={classes.resselButton}
              data-cy="retail-button"
              variant="contained"
              color="primary"
              onClick={handleRetail}
              disabled={!enableRetailButton}
              startIcon={<AddBusinessFilledIcon className={classes.iconColor} />}
            >
              {getLiteral('1870')}
            </Button>
          ))}
      </Grid>

      {isSomeGofindAppUrl && !inIframe() && !localStorage.getItem('gofind-user-allow-cookies') && (
        <CookieBanner />
      )}
    </div>
  )
}

export default memo(AppContainer)
